"use client";

import * as React from "react";
import type {
  ConnectorInfo,
  ConnectionInfo,
  IntegrationsConnectConfig,
  IntegrationsConnectState,
} from "./types";

const DEFAULT_BASE_URL = "https://api.stack0.dev/v1";

interface UseIntegrationsOptions extends IntegrationsConnectConfig {}

interface UseIntegrationsReturn extends IntegrationsConnectState {
  connect: (connectorSlug: string, options?: { name?: string }) => Promise<void>;
  disconnect: (connectionId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useIntegrations(options: UseIntegrationsOptions): UseIntegrationsReturn {
  const { apiKey, baseUrl = DEFAULT_BASE_URL, category, connectors: connectorFilter, onConnected, onConnectionError, onDisconnected } = options;

  const [state, setState] = React.useState<IntegrationsConnectState>({
    connectors: [],
    connections: [],
    isLoading: true,
    error: null,
  });

  const fetchWithAuth = React.useCallback(
    async (path: string, init?: RequestInit) => {
      const response = await fetch(`${baseUrl}${path}`, {
        ...init,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
          ...init?.headers,
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || `Request failed: ${response.status}`);
      }

      return response.json();
    },
    [apiKey, baseUrl],
  );

  const fetchData = React.useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // Fetch connectors and connections in parallel
      const [connectorsData, connectionsData] = await Promise.all([
        fetchWithAuth(`/integrations/connectors${category ? `?category=${category}` : ""}`),
        fetchWithAuth("/integrations/connections"),
      ]);

      let filteredConnectors = connectorsData as ConnectorInfo[];

      // Apply connector filter if provided
      if (connectorFilter && connectorFilter.length > 0) {
        filteredConnectors = filteredConnectors.filter((c) => connectorFilter.includes(c.slug));
      }

      setState({
        connectors: filteredConnectors,
        connections: (connectionsData as ConnectionInfo[]).map((conn) => ({
          ...conn,
          createdAt: new Date(conn.createdAt),
        })),
        isLoading: false,
        error: null,
      });
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : "Failed to fetch integrations",
      }));
    }
  }, [fetchWithAuth, category, connectorFilter]);

  // Initial fetch
  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const connect = React.useCallback(
    async (connectorSlug: string, options?: { name?: string }) => {
      try {
        // Create connection - this returns an authorization URL
        const result = await fetchWithAuth("/integrations/connections", {
          method: "POST",
          body: JSON.stringify({
            connectorSlug,
            name: options?.name,
          }),
        });

        // Open OAuth popup
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const popup = window.open(
          result.authorizationUrl,
          "Stack0 Connect",
          `width=${width},height=${height},left=${left},top=${top},popup=yes`,
        );

        if (!popup) {
          throw new Error("Failed to open authorization window. Please allow popups for this site.");
        }

        // Poll for popup close and check connection status
        const checkConnection = async () => {
          return new Promise<ConnectionInfo>((resolve, reject) => {
            const interval = setInterval(async () => {
              try {
                if (popup.closed) {
                  clearInterval(interval);

                  // Check connection status
                  const connection = await fetchWithAuth(`/integrations/connections/${result.connectionId}`);

                  if (connection.status === "connected") {
                    resolve({
                      ...connection,
                      createdAt: new Date(connection.createdAt),
                    });
                  } else if (connection.status === "error") {
                    reject(new Error("Connection failed. Please try again."));
                  } else {
                    reject(new Error("Connection was not completed."));
                  }
                }
              } catch (error) {
                clearInterval(interval);
                reject(error);
              }
            }, 500);

            // Timeout after 5 minutes
            setTimeout(() => {
              clearInterval(interval);
              popup.close();
              reject(new Error("Connection timed out."));
            }, 5 * 60 * 1000);
          });
        };

        const connection = await checkConnection();

        // Refresh data
        await fetchData();

        // Call onConnected callback
        onConnected?.(connection);
      } catch (error) {
        const err = error instanceof Error ? error : new Error("Connection failed");
        onConnectionError?.(err, connectorSlug);
        throw err;
      }
    },
    [fetchWithAuth, fetchData, onConnected, onConnectionError],
  );

  const disconnect = React.useCallback(
    async (connectionId: string) => {
      try {
        await fetchWithAuth(`/integrations/connections/${connectionId}`, {
          method: "DELETE",
        });

        // Refresh data
        await fetchData();

        // Call onDisconnected callback
        onDisconnected?.(connectionId);
      } catch (error) {
        throw error instanceof Error ? error : new Error("Failed to disconnect");
      }
    },
    [fetchWithAuth, fetchData, onDisconnected],
  );

  return {
    ...state,
    connect,
    disconnect,
    refresh: fetchData,
  };
}
